import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Upload, Image, Video, CheckCircle, Clock, AlertCircle } from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface Props {
  onClose: () => void;
  onSubmitted: () => void;
  userRole: "vendor" | "venue";
}

export function VendorOnboardingModal({ onClose, onSubmitted, userRole }: Props) {
  const [step, setStep] = useState<"form" | "submitted">("form");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [description, setDescription] = useState("");
  const [photos, setPhotos] = useState<File[]>([]);
  const [videos, setVideos] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);

  const photoRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLInputElement>(null);

  const roleLabel = userRole === "vendor" ? "Vendor" : "Venue";

  function handlePhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    setPhotos(prev => [...prev, ...files].slice(0, 10));
    const urls = files.map(f => URL.createObjectURL(f));
    setPhotoPreviews(prev => [...prev, ...urls].slice(0, 10));
  }

  function handleVideoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    setVideos(prev => [...prev, ...files].slice(0, 5));
  }

  function removePhoto(idx: number) {
    setPhotos(p => p.filter((_, i) => i !== idx));
    setPhotoPreviews(p => p.filter((_, i) => i !== idx));
  }

  function removeVideo(idx: number) {
    setVideos(v => v.filter((_, i) => i !== idx));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (!firstName.trim() || !lastName.trim()) {
      setError("First and last name are required.");
      return;
    }

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("firstName", firstName.trim());
      fd.append("lastName", lastName.trim());
      fd.append("description", description.trim());
      photos.forEach(f => fd.append("photos", f));
      videos.forEach(f => fd.append("videos", f));

      const res = await fetch(`${BASE}/api/business-profile`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const data = await res.json() as { error?: string };
      if (!res.ok) { setError(data.error ?? "Submission failed."); return; }

      setStep("submitted");
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={step === "submitted" ? onClose : undefined}
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.3 }}
        className="relative z-10 w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-sm bg-[#0d0b08] border border-[#d4af37]/30"
      >
        {/* Gold top bar */}
        <div className="h-1 w-full bg-gradient-to-r from-transparent via-[#d4af37] to-transparent" />

        <AnimatePresence mode="wait">
          {step === "form" ? (
            <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {/* Header */}
              <div className="flex items-start justify-between p-6 pb-4 border-b border-white/5">
                <div>
                  <p className="text-[10px] tracking-[0.3em] text-[#d4af37]/60 uppercase mb-1">Welcome to BookMySquadd</p>
                  <h2 className="text-xl font-serif text-gray-800">Complete Your {roleLabel} Profile</h2>
                  <p className="text-sm text-[#9a8f83] mt-1">
                    Your account is under review. Fill in your details to get started.
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-5">
                {/* Name row */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-[#9a8f83] uppercase tracking-widest mb-2">First Name *</label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={e => setFirstName(e.target.value)}
                      placeholder="Enter first name"
                      className="w-full bg-white/5 border border-white/10 rounded-sm px-3 py-2.5 text-sm text-gray-800 placeholder:text-[#9a8f83]/50 focus:outline-none focus:border-[#d4af37]/50 transition"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[#9a8f83] uppercase tracking-widest mb-2">Last Name *</label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={e => setLastName(e.target.value)}
                      placeholder="Enter last name"
                      className="w-full bg-white/5 border border-white/10 rounded-sm px-3 py-2.5 text-sm text-gray-800 placeholder:text-[#9a8f83]/50 focus:outline-none focus:border-[#d4af37]/50 transition"
                    />
                  </div>
                </div>

                {/* Account Status */}
                <div>
                  <label className="block text-xs text-[#9a8f83] uppercase tracking-widest mb-2">Account Status</label>
                  <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-sm px-3 py-2.5">
                    <Clock className="w-4 h-4 text-amber-400" />
                    <span className="text-sm text-amber-300 font-medium">Under Review</span>
                    <span className="text-xs text-amber-400/60 ml-1">— Pending admin approval</span>
                  </div>
                </div>

                {/* Photos */}
                <div>
                  <label className="block text-xs text-[#9a8f83] uppercase tracking-widest mb-2">
                    Photos <span className="text-[#9a8f83]/50 normal-case tracking-normal">(up to 10)</span>
                  </label>
                  <div
                    onClick={() => photoRef.current?.click()}
                    className="border border-dashed border-white/15 rounded-sm p-4 text-center cursor-pointer hover:border-[#d4af37]/40 transition"
                  >
                    <Image className="w-6 h-6 text-[#d4af37]/50 mx-auto mb-2" />
                    <p className="text-xs text-[#9a8f83]">Click to upload photos (JPG, PNG, WEBP)</p>
                  </div>
                  <input ref={photoRef} type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoChange} />
                  {photoPreviews.length > 0 && (
                    <div className="grid grid-cols-5 gap-2 mt-3">
                      {photoPreviews.map((url, i) => (
                        <div key={i} className="relative group aspect-square">
                          <img src={url} alt="" className="w-full h-full object-cover rounded-sm border border-white/10" />
                          <button
                            type="button"
                            onClick={() => removePhoto(i)}
                            className="absolute top-0.5 right-0.5 bg-black/70 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition"
                          >
                            <X className="w-3 h-3 text-gray-800" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Videos */}
                <div>
                  <label className="block text-xs text-[#9a8f83] uppercase tracking-widest mb-2">
                    Videos <span className="text-[#9a8f83]/50 normal-case tracking-normal">(up to 5, max 50MB each)</span>
                  </label>
                  <div
                    onClick={() => videoRef.current?.click()}
                    className="border border-dashed border-white/15 rounded-sm p-4 text-center cursor-pointer hover:border-[#d4af37]/40 transition"
                  >
                    <Video className="w-6 h-6 text-[#d4af37]/50 mx-auto mb-2" />
                    <p className="text-xs text-[#9a8f83]">Click to upload videos (MP4, MOV, WEBM)</p>
                  </div>
                  <input ref={videoRef} type="file" accept="video/*" multiple className="hidden" onChange={handleVideoChange} />
                  {videos.length > 0 && (
                    <div className="mt-3 space-y-1.5">
                      {videos.map((v, i) => (
                        <div key={i} className="flex items-center justify-between bg-white/5 border border-white/10 rounded-sm px-3 py-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <Video className="w-3.5 h-3.5 text-[#d4af37]/60 shrink-0" />
                            <span className="text-xs text-[#9a8f83] truncate">{v.name}</span>
                          </div>
                          <button type="button" onClick={() => removeVideo(i)} className="ml-2 text-[#9a8f83] hover:text-red-400 transition">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs text-[#9a8f83] uppercase tracking-widest mb-2">Description</label>
                  <textarea
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder="Tell us about your business, specialties, and experience..."
                    rows={4}
                    maxLength={1000}
                    className="w-full bg-white/5 border border-white/10 rounded-sm px-3 py-2.5 text-sm text-gray-800 placeholder:text-[#9a8f83]/50 focus:outline-none focus:border-[#d4af37]/50 transition resize-none"
                  />
                  <p className="text-right text-xs text-[#9a8f83]/50 mt-1">{description.length}/1000</p>
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-sm px-3 py-2">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    {error}
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 px-4 py-2.5 text-sm text-[#9a8f83] border border-white/10 rounded-sm hover:border-white/20 transition"
                  >
                    Fill in later
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm bg-[#d4af37] text-black font-semibold rounded-sm hover:bg-[#c9a227] transition disabled:opacity-60"
                  >
                    {loading ? (
                      <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    {loading ? "Submitting..." : "Submit for Review"}
                  </button>
                </div>
              </form>
            </motion.div>
          ) : (
            <motion.div
              key="submitted"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-10 text-center"
            >
              <div className="w-16 h-16 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center mx-auto mb-5">
                <CheckCircle className="w-8 h-8 text-[#d4af37]" />
              </div>
              <h2 className="text-xl font-serif text-gray-800 mb-2">Profile Submitted!</h2>
              <p className="text-sm text-[#9a8f83] mb-2">
                Your {roleLabel.toLowerCase()} profile is now <strong className="text-amber-300">under review</strong>.
              </p>
              <p className="text-xs text-[#9a8f83]/70 mb-6">
                Our team will review your details and you'll receive an email confirmation once approved. This usually takes 24–48 hours.
              </p>
              <button
                onClick={() => { onSubmitted(); onClose(); }}
                className="px-6 py-2.5 bg-[#d4af37] text-black text-sm font-semibold rounded-sm hover:bg-[#c9a227] transition"
              >
                Got it, continue
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
